import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import store from "../store";
import {setTemporaryNotifications} from "../notifications/actions";
import {defaultNotificationCreator} from "../../templates/Notifications";
import stocksReserveService from "../../api/services/stocksReserveService";
import {IColumnData, IRowData} from "../../constants/Table/interfaces";
import {IStocksReserveForWarehouse} from "../../constants/StocksReserve/IStocksReserveForWarehouse";

export interface IEditingWarehouseReserve {
    sellerID: number;
    warehouseID: string;
    productID: number;
    editQ: number;
    reasonDescription: string;
    data: IStocksReserveForWarehouse;
}

interface IStocksReserveReducer {
    rowData: IRowData[];
    columnData: IColumnData[];
    isNeedUpdateData: boolean;
    isStocksReserveFormModalOpen: boolean;
    isReserveEditingFormModalOpen: boolean;
    isNeedClearReserveEditForm: boolean;
    idFilter: number|null;
    skuFilter: string|null;
    barcodeFilter: string|null;
    sizeFilter: string|null;
    nameFilter: string|null;
    brandFilter: string|null;
    notesFilter: string|null;
    greaterThanZeroFilter: string|null;
    editingWarehouseReserve: IEditingWarehouseReserve|null;
}

const initialState: IStocksReserveReducer = {
    rowData: [],
    columnData: [],
    isStocksReserveFormModalOpen: false,
    isReserveEditingFormModalOpen: false,
    isNeedClearReserveEditForm: false,
    isNeedUpdateData: false,
    idFilter: null,
    skuFilter: null,
    barcodeFilter: null,
    sizeFilter: null,
    nameFilter: null,
    brandFilter: null,
    notesFilter: null,
    greaterThanZeroFilter: 'reserved',
    editingWarehouseReserve: null
}

export const saveStocksReserveSettings = createAsyncThunk(
    'stocksReserve/saveSettings',
    async (payload:any) => {
        try {
            const data = payload
            console.log(`updateStocksReserve`, data);
            const res = await stocksReserveService.saveSettings(data);

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер при сохранении настроек резервирования. status: " + res.status)
            }

        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось применить настройки резервирования",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

export const writeOffVacantReserves = createAsyncThunk(
    'stocksReserve/writeOffReserves',
    async (payload:IEditingWarehouseReserve) => {
        try {
            const res = await stocksReserveService.writeOffVacantReserves({
                sellerID: payload.sellerID,
                warehouseID: payload.warehouseID,
                quantitiesByProduct: { [payload.productID.toString()]: payload.editQ },
                reasonDescription: payload.reasonDescription,
                initiator: "user",
                IsRecalcDistributedStocks: true
            });

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }

            store.dispatch(setIsReserveEditingFormModalOpen(false))
            store.dispatch(setIsNeedUpdateData(true))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Резервы успешно списаны",
                    description: "",
                    iconType: "success"
                })
            ))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось списать резервы",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)

export const cancelVacantReserves = createAsyncThunk(
    'stocksReserve/cancelReserves',
    async (payload:IEditingWarehouseReserve) => {
        try {
            const res = await stocksReserveService.cancelVacantReserves({
                sellerID: payload.sellerID,
                warehouseID: payload.warehouseID,
                quantity:  payload.editQ,
                productID:  payload.productID,
                reasonDescription: payload.reasonDescription,
                initiator: "user",
                IsRecalcDistributedStocks: true
            });

            if (res.status > 300) {
                throw new Error("Ошибка при запросе на сервер status: " + res.status)
            }

            store.dispatch(setIsReserveEditingFormModalOpen(false))
            store.dispatch(setIsNeedUpdateData(true))
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Резервы успешно списаны",
                    description: "",
                    iconType: "success"
                })
            ))
        } catch (error: any) {
            console.error(error.message)
            store.dispatch(setTemporaryNotifications(
                //@ts-ignore
                defaultNotificationCreator({
                    title: "Не удалось списать резервы",
                    description: "Пожалуйста, обратитесь в поддержку",
                    iconType: "warning"
                })
            ))
        }
    }
)


const stocksReserveStateSlice = createSlice({
    name: 'stocksReserve',
    initialState,
    reducers: {
        /**
         * @description Setting rows
         * @param state
         * @param action
         */
        setRowData(state, action: PayloadAction<IRowData[]>) {
            state.rowData = action.payload;
        },
        editRowDataElement(state, action: PayloadAction<IRowData>) {
            state.rowData = state.rowData.map((element) =>
                element.id !== action.payload.id ? element : action.payload
            );
        },
        /**
         * @description Set flag that means is data must be updated
         * @param state
         * @param action
         */
        setIsNeedUpdateData(state, action: PayloadAction<boolean>) {
            state.isNeedUpdateData = action.payload;
        },
        setIdFilter(state, action: PayloadAction<number>) {
            state.idFilter = action.payload;
        },
        setSkuFilter(state, action: PayloadAction<string>) {
            state.skuFilter = action.payload;
        },
        setBarcodeFilter(state, action: PayloadAction<string>) {
            state.barcodeFilter = action.payload;
        },
        setSizeFilter(state, action: PayloadAction<string>) {
            state.sizeFilter = action.payload;
        },
        setNameFilter(state, action: PayloadAction<string>) {
            state.nameFilter = action.payload;
        },
        setBrandFilter(state, action: PayloadAction<string>) {
            state.brandFilter = action.payload;
        },
        setNotesFilter(state, action: PayloadAction<string>) {
            state.notesFilter = action.payload;
        },
        setGreaterThanZeroFilter(state, action: PayloadAction<string>) {
            state.greaterThanZeroFilter = action.payload;
        },
        setEditingWarehouseReserve(state, action: PayloadAction<IEditingWarehouseReserve>) {
            state.editingWarehouseReserve = action.payload;
        },
        resetEditingRow(state) {
            state.editingWarehouseReserve = null;
        },
        setIsReserveEditingFormModalOpen(state, action: PayloadAction<boolean>) {
            state.isReserveEditingFormModalOpen = action.payload;
        },
        setIsNeedClearReserveEditForm(state, action: PayloadAction<boolean>) {
            state.isNeedClearReserveEditForm = action.payload;
        },
    },
});

const stocksReserve = stocksReserveStateSlice.reducer;

export const {
    setRowData,
    editRowDataElement,
    setIdFilter,
    setSkuFilter,
    setBarcodeFilter,
    setSizeFilter,
    setNameFilter,
    setBrandFilter,
    setNotesFilter,
    setGreaterThanZeroFilter,
    setEditingWarehouseReserve,
    resetEditingRow,
    setIsNeedUpdateData,
    setIsReserveEditingFormModalOpen,
    setIsNeedClearReserveEditForm,
} = stocksReserveStateSlice.actions;

export default stocksReserve